import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { loadCampaignReport, type ReportProgress } from '@/lib/campaignReport/load';
import { generateCampaignReportAnalysis } from '@/lib/campaignReport/analysis';
import { CampaignReportError } from '@/lib/campaignReport/types';

type Stage = 'idle' | 'loading' | 'analyzing' | 'rendering';
type Failure = CampaignReportError['code'] | 'pdf';

export function CampaignReportButton({ campaignId, disabled = false }: {
  campaignId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const active = useRef<AbortController | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [progress, setProgress] = useState<ReportProgress | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    setStage('idle');
    setProgress(null);
    setFailure(null);
    return () => {
      active.current?.abort();
      active.current = null;
    };
  }, [campaignId]);

  const download = async () => {
    if (active.current || disabled || !campaignId) return;
    const controller = new AbortController();
    active.current = controller;
    const isCurrent = () => active.current === controller && !controller.signal.aborted;
    let failureSource: Failure = 'load';
    setFailure(null);
    setProgress(null);
    setStage('loading');
    try {
      const report = await loadCampaignReport(client, campaignId, controller.signal, (next) => {
        if (isCurrent()) setProgress(next);
      });
      if (!isCurrent()) return;
      setStage('analyzing');
      failureSource = 'ai_failed';
      const analysis = await generateCampaignReportAnalysis(client, report, controller.signal);
      if (!isCurrent()) return;
      setStage('rendering');
      failureSource = 'pdf';
      const { createCampaignReportPdf, reportFilename } = await import('@/lib/campaignReport/pdf');
      if (!isCurrent()) return;
      const pdf = createCampaignReportPdf(report, analysis);
      if (!isCurrent()) return;
      await pdf.save(reportFilename(report), { returnPromise: true });
    } catch (error) {
      if (isCurrent()) setFailure(error instanceof CampaignReportError ? error.code : failureSource);
    } finally {
      if (isCurrent()) {
        active.current = null;
        setStage('idle');
        setProgress(null);
      }
    }
  };

  const label = stage === 'rendering'
    ? t('campaignAudit.report.rendering')
    : stage === 'analyzing'
      ? t('campaignAudit.report.analyzing')
      : stage === 'loading'
        ? progress ? t('campaignAudit.report.progress', progress) : t('campaignAudit.report.collecting')
        : t('campaignAudit.report.download');

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={download}
        disabled={disabled || !campaignId || stage !== 'idle'}
        aria-busy={stage !== 'idle'}
        title={t('campaignAudit.report.scope')}
      >
        {stage === 'idle' ? <Download className="h-4 w-4" aria-hidden="true" />
          : <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {label}
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {stage !== 'idle' ? label : ''}
      </span>
      <Dialog open={failure !== null} onOpenChange={(open) => { if (!open) setFailure(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('campaignAudit.report.errorTitle')}</DialogTitle>
            <DialogDescription>{failure ? t(`campaignAudit.report.errors.${failure}`) : ''}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailure(null)}>{t('common.close')}</Button>
            <Button onClick={download} disabled={disabled || !campaignId}>{t('campaignAudit.report.retry')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
